/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Antigravity / Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: QueueSelect.tsx
 🗓️ Created: 2026-03-21
 🧩 Description:
     Searchable queue selector. Accepts a list of queues as props
     (already loaded by the parent hook) and renders a dropdown
     with an inline search input — consistent with AgentSelect / DateRangePicker.
     Uses createPortal + position:fixed so it always escapes any
     overflow:hidden parent container.
────────────────────────────────────────────────────────────────*/

import React, { useState, useMemo, useCallback, useEffect, useRef, memo } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search, Phone } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';

export interface QueueOption {
  id: string;
  name: string;
}

interface QueueSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: QueueOption[];
  allLabel?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const DROPDOWN_W = 260;
const DROPDOWN_H = 320;

export const QueueSelect: React.FC<QueueSelectProps> = ({
  value,
  onChange,
  options,
  allLabel,
  placeholder,
  className = '',
  disabled = false,
}) => {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropPos, setDropPos] = useState({ top: 0, left: 0 });

  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownId = 'queue-select-dropdown';

  const allQueuesLabel = allLabel || t('queueCallbackReport.filters.allQueues') || 'All Queues';

  // ── Calculate dropdown position from trigger (fixed, viewport-aware) ─────────
  const calcPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const overflowBottom = rect.bottom + 4 + DROPDOWN_H > window.innerHeight;
    const overflowRight  = rect.left + DROPDOWN_W > window.innerWidth;
    setDropPos({
      top:  overflowBottom ? rect.top - DROPDOWN_H - 4 : rect.bottom + 4,
      left: overflowRight  ? rect.right - DROPDOWN_W   : rect.left,
    });
  }, []);

  // ── Close on outside click and recalc on scroll/resize ───────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const dropEl = document.getElementById(dropdownId);
      if (
        triggerRef.current && !triggerRef.current.contains(e.target as Node) &&
        !(dropEl && dropEl.contains(e.target as Node))
      ) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    window.addEventListener('scroll', calcPosition, true);
    window.addEventListener('resize', calcPosition);
    return () => {
      document.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', calcPosition, true);
      window.removeEventListener('resize', calcPosition);
    };
  }, [isOpen, calcPosition]);

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const q = searchQuery.toLowerCase();
    return options.filter(o => o.name.toLowerCase().includes(q));
  }, [options, searchQuery]);

  const selectedLabel = useMemo(() => {
    if (!value) return null;
    return options.find(o => o.id === value)?.name ?? value;
  }, [options, value]);

  const handleSelect = useCallback((id: string) => {
    onChange(id);
    setIsOpen(false);
    setSearchQuery('');
  }, [onChange]);

  const handleToggle = () => {
    if (disabled) return;
    if (!isOpen) {
      calcPosition();
      setSearchQuery('');
    }
    setIsOpen(o => !o);
  };

  // ── Dropdown portal ───────────────────────────────────────────────────────────
  const dropdown = isOpen ? createPortal(
    <div
      id={dropdownId}
      style={{
        position: 'fixed',
        top:  dropPos.top,
        left: dropPos.left,
        width: DROPDOWN_W,
        zIndex: 9999,
      }}
      className="bg-card border border-border rounded-lg shadow-xl overflow-hidden"
    >
      {/* Search Input */}
      <div className="p-2 border-b border-border">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            autoFocus
            placeholder={t('queueCallbackReport.filters.searchQueue') || 'Search queue...'}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>

      {/* Options List */}
      <div className="max-h-60 overflow-y-auto">
        {/* All Queues option */}
        <button
          type="button"
          onClick={() => handleSelect('')}
          className={`w-full px-3 py-2 text-sm text-left hover:bg-muted transition-colors flex items-center gap-2 ${
            !value ? 'bg-primary/10 text-primary font-medium' : 'text-foreground'
          }`}
        >
          <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span>{allQueuesLabel}</span>
        </button>

        {filteredOptions.length === 0 ? (
          <div className="px-3 py-4 text-sm text-muted-foreground text-center">
            {t('queueCallbackReport.filters.noQueuesFound') || 'No queues found'}
          </div>
        ) : (
          filteredOptions.map(opt => (
            <button
              key={opt.id}
              type="button"
              onClick={() => handleSelect(opt.id)}
              className={`w-full px-3 py-2 text-sm text-left hover:bg-muted transition-colors flex items-center gap-2 ${
                value === opt.id ? 'bg-primary/10 text-primary font-medium' : 'text-foreground'
              }`}
            >
              <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="truncate">{opt.name}</span>
            </button>
          ))
        )}
      </div>

      {/* Footer */}
      {filteredOptions.length > 0 && (
        <div className="px-3 py-1.5 text-xs text-muted-foreground border-t border-border bg-muted/30">
          {filteredOptions.length} {t('common.results') || 'results'}
        </div>
      )}
    </div>,
    document.body
  ) : null;

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`
          w-full px-3 py-2 text-sm border rounded bg-background text-foreground h-9
          focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent
          flex items-center justify-between gap-2
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-muted/50'}
          ${isOpen ? 'ring-1 ring-primary border-transparent' : 'border-input'}
          ${className}
        `}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Phone className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          <span className={`truncate text-left ${selectedLabel ? 'text-foreground' : 'text-muted-foreground'}`}>
            {selectedLabel ?? (placeholder ?? allQueuesLabel)}
          </span>
        </div>
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {dropdown}
    </div>
  );
};

export default memo(QueueSelect);
